import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../Layouts/Spinner";
import ReactSelect from "react-select";
import { Card, CardBody } from "reactstrap";
import * as yup from "yup";
import * as XLSX from "xlsx";
import swal from "sweetalert";
import { getUserList } from "../../../redux/invoice/action";
import { exportAllReports, getAllReports } from "../../../redux/service/action";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import { getMembershipList } from "../../../redux/membership/action";
import { formatPurchaseSourceLabel, getMembershipPlanLabel, getMembershipTermLabel } from "../../Common/Utils/adminViewUtils";
import { formatDate } from "../../Common/Utils/dateUtils";
import TestingAccountFilter, { getTestingAccountFilterLabel } from "../../Common/TestingAccountFilter";
import "../../Common/Styles/AdminEnhancements.css";

export const invoiceReportValidation = () =>
  yup.object({
    start_date: yup.date(),
    end_date: yup
      .date()
      .min(yup.ref("start_date"), "End date cannot be before start date"),
    invoice_type: yup.number(),
    user_id: yup.string(),
  });

const TransactionReport = () => {
  const dispatch = useDispatch();
  const [endDates, setEndDates] = useState("");
  const [loading, setLoading] = useState(false);
  const [startDates, setStartDates] = useState("");
  const [reportsData, setReportsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [exportLoader, setExportLoader] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [theFilterInfo, setTheFilterInfo] = useState({
    user_id: "",
    start_date: "",
    end_date: "",
    membership_id: "",
    testing_account_filter: "hide",
    type: "transaction_report",
  });

  const [params, setParams] = useState({
    type: "transaction_report",
    search_key: "",
    status: "",
    page: 1,
    per_page: 10,
    testing_account_filter: "hide",
  });

  const userList = useSelector((state) => state?.reusableSlice?.userList);
  const membershipList = useSelector(
    (state) => state?.reusableSlice?.membershipList
  );

  const companyOptions = userList?.users?.map((item) => ({
    value: item?.id,
    label: item?.company_name,
  }));

  const membershipOptions = membershipList?.memberships?.map((item) => ({
    value: item?.id,
    label: `${item?.title} (${getMembershipPlanLabel(item)})`,
  }));

  useEffect(() => {
    dispatch(getUserList({ search_key: "" }));
    dispatch(getMembershipList({ search_key: "" }));
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: "",
      start_date: "",
      end_date: "",
      membership_id: "",
      testing_account_filter: "hide",
      type: "transaction_report",
    },
    validationSchema: invoiceReportValidation(),
    onSubmit: (values) => {
      if (
        values.user_id ||
        values.membership_id ||
        values.start_date ||
        values.end_date ||
        values.testing_account_filter !== "hide"
      ) {
        handleSubmit(values);
      }
    },
  });

  const handleGetReports = (data) => {
    setIsLoading(true);
    getAllReports(data)
      .then((res) => {
        if (res.data.status) {
          setIsLoading(false);
          setReportsData(res.data.response || {});
        }
      })
      .catch((e) => {
        console.log(e, "error in get report");
        setIsLoading(false);
      });
  };

  const handleExportReport = () => {
    setExportLoader(true);
    exportAllReports(theFilterInfo)
      .then((response) => {
        if (response.data) {
          setExportLoader(false);
          const header = [[
            "Sl",
            "Company",
            "Transaction ID",
            "Customer Name",
            "Membership",
            "Term",
            "Source",
            "Price",
          ]];

          const data = (response.data.transaction_report || []).map((prop, index) => [
            index + 1,
            prop.company_name,
            prop.transaction_id,
            `${prop.user_firstname} ${prop.user_lastname}`,
            `${prop.membership_title}`,
            getMembershipTermLabel(prop),
            formatPurchaseSourceLabel(prop.purchase_source_label),
            prop.price,
          ]);

          const worksheet = XLSX.utils.aoa_to_sheet(header);
          XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A2" });

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
          XLSX.writeFile(workbook, "TransactionReports.xlsx");
        }
      })
      .catch((e) => {
        console.log(e, "while exporting reports");
        swal({
          text: e.message,
          icon: "error",
          button: "Close",
          timer: 1500,
        });
        setExportLoader(false);
      });
  };

  useEffect(() => {
    handleGetReports(params);
  }, [params]);

  const handleSubmit = async (values) => {
    const data = {
      ...values,
      page: 1,
      per_page: 10,
    };
    setParams((prev) => ({
      ...prev,
      page: 1,
      ...values,
    }));
    setTheFilterInfo(values);
    handleGetReports(data);
  };

  const handleClearFilter = () => {
    setSelectedCompany(null);
    setStartDates("");
    setEndDates("");
    setSelectedMembership(null);
    formik.resetForm();
    setTheFilterInfo({
      user_id: "",
      start_date: "",
      end_date: "",
      membership_id: "",
      status: "",
      testing_account_filter: "hide",
      type: "transaction_report",
    });
    setParams({
      type: "transaction_report",
      search_key: "",
      status: "",
      page: 1,
      per_page: 10,
      testing_account_filter: "hide",
    });
    handleGetReports({
      user_id: "",
      start_date: "",
      end_date: "",
      membership_id: "",
      status: "",
      page: 1,
      per_page: 10,
      testing_account_filter: "hide",
      type: "transaction_report",
    });
  };

  const rows = reportsData?.transaction_report || [];

  const summary = useMemo(() => {
    const revenue = rows.reduce((sum, item) => sum + Number(item?.price || 0), 0);
    const uniqueCustomers = new Set(rows.map((item) => item?.company_name).filter(Boolean)).size;
    const shopify = rows.filter(
      (item) => formatPurchaseSourceLabel(item?.purchase_source_label) === "Shopify"
    ).length;

    return [
      { label: "Visible transactions", value: rows.length, subtext: "Rows currently shown" },
      { label: "Visible revenue", value: revenue.toFixed(2), subtext: "Sum of prices on screen" },
      { label: "Unique companies", value: uniqueCustomers, subtext: "Distinct companies in results" },
      { label: "Shopify purchases", value: shopify, subtext: "Rows sourced from Shopify" },
    ];
  }, [rows]);

  const activeFilterChips = [
    selectedCompany?.label && { label: "Company", value: selectedCompany.label },
    selectedMembership?.label && { label: "Membership", value: selectedMembership.label },
    formik.values.testing_account_filter !== "hide" && {
      label: "Account Type",
      value: getTestingAccountFilterLabel(formik.values.testing_account_filter),
    },
    startDates && { label: "Start", value: formatDate(startDates) },
    endDates && { label: "End", value: formatDate(endDates) },
  ].filter(Boolean);

  return (
    <Card>
      <div className="p-3">
        <div className="category-wrapper">
          <div className="admin-toolbar">
            <div>
              <h4 className="mb-1">Transaction Report</h4>
              <div className="admin-toolbar-note">
                Better at-a-glance visibility for source, term, and transaction totals.
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="submit-btn filter-btn mx-5"
                onClick={() => setShowFilter(!showFilter)}
              >
                <span>
                  <i className="fa-solid fa-filter"></i>{" "}
                  {showFilter ? "Hide Filter" : "Filter"}
                </span>
              </button>
              <button
                disabled={exportLoader}
                type="button"
                className="submit-btn"
                onClick={handleExportReport}
                style={{ width: "max-content" }}
              >
                {exportLoader ? <Spinner /> : "Export"}
              </button>
            </div>
          </div>

          <div className="admin-summary-grid">
            {summary.map((item) => (
              <div className="admin-summary-card" key={item.label}>
                <div className="admin-summary-label">{item.label}</div>
                <div className="admin-summary-value">{item.value}</div>
                <div className="admin-summary-subtext">{item.subtext}</div>
              </div>
            ))}
          </div>

          {activeFilterChips.length > 0 && (
            <div className="admin-meta-row">
              {activeFilterChips.map((chip) => (
                <div className="admin-chip" key={`${chip.label}-${chip.value}`}>
                  <strong>{chip.label}:</strong> {chip.value}
                </div>
              ))}
              <button
                type="button"
                className="submit-btn"
                onClick={handleClearFilter}
                style={{ width: "max-content" }}
              >
                Reset All Filters
              </button>
            </div>
          )}

          {showFilter && (
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-3 form-group">
                  <label>Select Company</label>
                  <ReactSelect
                    isClearable
                    value={selectedCompany}
                    onChange={(selected) => {
                      setSelectedCompany(selected || null);
                      formik.setFieldValue("user_id", selected?.value || "");
                    }}
                    options={companyOptions}
                    placeholder="Select Company"
                  />
                  {formik.touched.user_id && formik.errors.user_id ? (
                    <div className="error">{formik.errors.user_id}</div>
                  ) : null}
                </div>
                <div className="col-md-3 form-group">
                  <label>Select Membership</label>
                  <ReactSelect
                    isClearable
                    value={selectedMembership}
                    onChange={(selected) => {
                      setSelectedMembership(selected || null);
                      formik.setFieldValue("membership_id", selected?.value || "");
                    }}
                    options={membershipOptions}
                    placeholder="Select membership"
                  />
                  {formik.touched.membership_id && formik.errors.membership_id ? (
                    <div className="error">{formik.errors.membership_id}</div>
                  ) : null}
                </div>
                <div className="col-md-3 form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter start date"
                    value={startDates}
                    onChange={(event) => {
                      setStartDates(event.target.value);
                      formik.setFieldValue("start_date", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    name="start_date"
                  />
                  {formik.touched.start_date && formik.errors.start_date ? (
                    <div className="error">{formik.errors.start_date}</div>
                  ) : null}
                </div>

                <div className="col-md-3 form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter end date"
                    value={endDates}
                    onChange={(event) => {
                      setEndDates(event.target.value);
                      formik.setFieldValue("end_date", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    min={startDates || undefined}
                    name="end_date"
                  />
                  {formik.touched.end_date && formik.errors.end_date ? (
                    <div className="error">{formik.errors.end_date}</div>
                  ) : null}
                </div>
                <TestingAccountFilter
                  value={formik.values.testing_account_filter}
                  onChange={formik.handleChange}
                />
                <div
                  className="d-flex align-items-center justify-content-center mt-3 col-md-12"
                  style={{ columnGap: "20px" }}
                >
                  <button
                    className="submit-btn"
                    style={{ width: "max-content" }}
                    type="submit"
                  >
                    {!loading ? "Apply Filters" : <Spinner />}
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleClearFilter}
                    style={{ width: "max-content" }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="table-sec px-3">
          <table className="table border-table-sec">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Company</th>
                <th>Transaction ID</th>
                <th>Customer Name</th>
                <th scope="col">Membership</th>
                <th scope="col">Term</th>
                <th scope="col">Source</th>
                <th scope="col">Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((item, index) => (
                  <tr key={item?.transaction_id || index}>
                    <td>{index + 1 + (params?.page - 1) * 10}</td>
                    <td>{item.company_name}</td>
                    <td>{item.transaction_id}</td>
                    <td>
                      {item.user_firstname} {item.user_lastname}
                    </td>
                    <td>{item?.membership_title}</td>
                    <td>
                      <span className="admin-chip admin-chip--info">{getMembershipTermLabel(item)}</span>
                    </td>
                    <td>
                      <span className="admin-chip">{formatPurchaseSourceLabel(item?.purchase_source_label)}</span>
                    </td>
                    <td>{item.price}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    Transaction reports are Not Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardBody>
      )}
      <CommonPagination
        params={params}
        setParams={setParams}
        pagination_meta={reportsData?.pagination_meta}
      />
    </Card>
  );
};

export default TransactionReport;
